import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth.store';

/**
 * Pinta el sistema con el color que eligió la empresa.
 *
 * El campo «Color de marca» existía en Configuración desde el principio: se
 * guardaba en la base y no hacía absolutamente nada. Quien lo cambiaba veía
 * que el sistema seguía verde y daba por hecho que estaba roto.
 *
 * Todo el tema cuelga de la variable `--primary`, así que basta con
 * reescribirla: botones, enlaces, etiquetas, la agenda y los gráficos siguen
 * el color sin tocar una sola pantalla.
 *
 * Se escribe en el <html> y no en una hoja de estilos porque cambia por
 * empresa y en caliente, al guardar, sin recargar.
 */

/** #10b981 → { h: 160, s: 84, l: 45 }, que es lo que espera Tailwind aquí. */
function hexAHsl(hex: string): { h: number; s: number; l: number } | null {
  const limpio = hex.trim().replace('#', '');
  // Admite el formato corto (#0f0) porque un selector de color puede darlo.
  const full = limpio.length === 3
    ? limpio.split('').map((c) => c + c).join('')
    : limpio;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }

  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

export function ColorDeMarca() {
  const brandColor = useAuthStore((s) => s.tenant?.brandColor);

  useEffect(() => {
    const raiz = document.documentElement;
    // Las variables que se tocan. Al quitarlas vuelve el verde del CSS.
    const vars = ['--primary', '--ring', '--accent'];

    if (!brandColor) {
      vars.forEach((v) => raiz.style.removeProperty(v));
      return;
    }

    const hsl = hexAHsl(brandColor);
    if (!hsl) return;   // Un color mal escrito no debe dejar el sistema en blanco.

    const { h, s, l } = hsl;
    raiz.style.setProperty('--primary', `${h} ${s}% ${l}%`);
    raiz.style.setProperty('--ring', `${h} ${s}% ${l}%`);
    // El acento va un poco más claro y saturado: es el mismo color pero se
    // usa en fondos, donde el tono pleno resulta pesado.
    raiz.style.setProperty('--accent', `${h} ${Math.min(100, s + 5)}% ${Math.min(92, l + 12)}%`);

    // El texto encima del color: blanco sobre oscuro, oscuro sobre claro. Sin
    // esto, un amarillo de marca deja los botones con letra blanca ilegible.
    raiz.style.setProperty(
      '--primary-foreground',
      l > 62 ? '222 47% 11%' : '0 0% 100%',
    );

    return () => vars.forEach((v) => raiz.style.removeProperty(v));
  }, [brandColor]);

  return null;
}
